import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { animate, motion, useInView, useMotionValue, useMotionValueEvent, useReducedMotion, useScroll, useSpring, useTransform, } from 'framer-motion';
/* ============================================================================
 * Shared motion system for the AI Interview landing page.
 *
 * Every scroll-driven scene reads ONE MotionValue (`progress`, 0 → 1) from its
 * StickyStory. Where that value comes from depends on the device:
 *
 *   scroll  – wide screens: the scene is pinned and progress follows scroll.
 *   auto    – narrow/touch screens: the scene is not pinned; progress plays
 *             on a timer once the scene is in view (a mobile-specific
 *             composition rather than a shrunken pinned one).
 *   static  – prefers-reduced-motion: progress is fixed at 1 (final state).
 *
 * Scenes only ever derive transforms/opacity from `progress`, or switch
 * discrete stages through useStage(), so one listener drives each scene.
 * ========================================================================== */
export const EASE_OUT = [0.16, 1, 0.3, 1];
export const EASE_IN_OUT = [0.65, 0, 0.35, 1];
export const SPRING = { type: 'spring', stiffness: 160, damping: 24, mass: 0.9 };
export const SPRING_SOFT = { type: 'spring', stiffness: 80, damping: 18, mass: 1 };
export const cx = (...c) => c.filter(Boolean).join(' ');
/** Wide enough (and tall enough) to pin a full-viewport scene. */
const PIN_QUERY = '(min-width: 960px) and (min-height: 600px)';
export function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
    useEffect(() => {
        const mql = window.matchMedia(query);
        const onChange = () => setMatches(mql.matches);
        onChange();
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, [query]);
    return matches;
}
export function useStoryMode() {
    const reduced = useReducedMotion();
    const canPin = useMediaQuery(PIN_QUERY);
    if (reduced)
        return 'static';
    return canPin ? 'scroll' : 'auto';
}
const StoryContext = createContext(null);
export function useStory() {
    const ctx = useContext(StoryContext);
    if (!ctx)
        throw new Error('useStory must be used inside <StickyStory>');
    return ctx;
}
export function StickyStory({ id, length = 3, autoDuration = 9, loop = false, className, stageClassName, theme = 'light', chapter, labelledBy, children, }) {
    const ref = useRef(null);
    const mode = useStoryMode();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
    const auto = useMotionValue(mode === 'static' ? 1 : 0);
    const inView = useInView(ref, { amount: 0.3 });
    useEffect(() => {
        if (mode === 'static') {
            auto.set(1);
            return undefined;
        }
        if (mode !== 'auto' || !inView)
            return undefined;
        // Resume from wherever the last play left off; restart when finished.
        if (auto.get() >= 1)
            auto.set(0);
        const controls = animate(auto, 1, {
            duration: autoDuration * (1 - auto.get()),
            ease: 'linear',
            repeat: loop ? Infinity : 0,
            repeatDelay: 1.2,
        });
        return () => controls.stop();
    }, [mode, inView, auto, autoDuration, loop]);
    const progress = mode === 'scroll' ? scrollYProgress : auto;
    const value = useMemo(() => ({ progress, mode }), [progress, mode]);
    return (_jsx("section", { id: id, ref: ref, className: cx('vx-story', className), "data-mode": mode, "data-theme": theme, "data-chapter": chapter, "aria-labelledby": labelledBy, style: mode === 'scroll' ? { height: `${length * 100}vh` } : undefined, children: _jsx("div", { className: cx('vx-story__stage', stageClassName), children: _jsx(StoryContext.Provider, { value: value, children: children }) }) }));
}
/**
 * Maps a 0→1 MotionValue onto discrete stages. `steps` are ascending
 * thresholds: [0.3, 0.6] → stage 0 below 0.3, 1 below 0.6, else 2.
 * Re-renders only when the stage actually changes.
 */
export function useStage(progress, steps) {
    const stepsRef = useRef(steps);
    stepsRef.current = steps;
    const calc = useCallback((v) => {
        let s = 0;
        for (const t of stepsRef.current)
            if (v >= t)
                s += 1;
        return s;
    }, []);
    const [stage, setStage] = useState(() => calc(progress.get()));
    useMotionValueEvent(progress, 'change', (v) => {
        const next = calc(v);
        setStage((prev) => (prev === next ? prev : next));
    });
    useEffect(() => setStage(calc(progress.get())), [progress, calc]);
    return stage;
}
/**
 * useTransform over a sub-range of a 0→1 progress value, padded so the range
 * always spans 0…1. Framer Motion 12 hands scroll-linked opacity/transform to a
 * native scroll timeline; when the keyframes stop short of 0 or 1 the browser
 * fills the gap with the element's underlying value, so a fade-in would fade
 * back out after its range. Explicit end keyframes hold the clamped value.
 */
export function useRange(progress, input, output) {
    const i = [...input];
    const o = [...output];
    if (i[0] > 0) {
        i.unshift(0);
        o.unshift(o[0]);
    }
    if (i[i.length - 1] < 1) {
        i.push(1);
        o.push(o[o.length - 1]);
    }
    return useTransform(progress, i, o);
}
/** Normalises a sub-range of progress to 0→1 (clamped). */
export function segment(v, start, end) {
    if (v <= start)
        return 0;
    if (v >= end)
        return 1;
    return (v - start) / (end - start);
}
/* --------------------------------- Reveal --------------------------------- */
const revealVariants = {
    up: { hidden: { opacity: 0, y: 32 }, show: { opacity: 1, y: 0 } },
    fade: { hidden: { opacity: 0 }, show: { opacity: 1 } },
    blur: {
        hidden: { opacity: 0, y: 18, filter: 'blur(10px)' },
        show: { opacity: 1, y: 0, filter: 'blur(0px)' },
    },
    scale: { hidden: { opacity: 0, scale: 0.96 }, show: { opacity: 1, scale: 1 } },
};
export function Reveal({ children, variant = 'up', delay = 0, className, as = 'div', amount = 0.25, }) {
    const reduced = useReducedMotion();
    const Comp = motion[as];
    return (_jsx(Comp, { className: className, initial: 'hidden', whileInView: 'show', viewport: { once: true, amount }, variants: reduced ? revealVariants.fade : revealVariants[variant], transition: { duration: reduced ? 0.3 : 0.9, ease: EASE_OUT, delay: reduced ? 0 : delay }, children: children }));
}
/* ------------------------------- TextReveal -------------------------------
 * Splits a line into masked words (or characters) that rise into place.
 * Screen readers get the plain string via aria-label.
 * ------------------------------------------------------------------------ */
export function TextReveal({ text, by = 'word', className, delay = 0, stagger, play, as = 'span', }) {
    const reduced = useReducedMotion();
    const Comp = motion[as];
    const step = stagger !== null && stagger !== void 0 ? stagger : (by === 'char' ? 0.028 : 0.06);
    const words = text.split(' ');
    let index = 0;
    const container = {
        hidden: {},
        show: { transition: { staggerChildren: reduced ? 0 : step, delayChildren: delay } },
    };
    const piece = reduced
        ? { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.3 } } }
        : {
            hidden: { y: '108%', rotate: 4 },
            show: { y: '0%', rotate: 0, transition: { duration: 1, ease: EASE_OUT } },
        };
    const trigger = play === undefined
        ? { initial: 'hidden', whileInView: 'show', viewport: { once: true, amount: 0.4 } }
        : { initial: 'hidden', animate: play ? 'show' : 'hidden' };
    return (_jsx(Comp, Object.assign({ className: cx('vx-split', className), "aria-label": text, variants: container }, trigger, { children: words.map((word, wi) => (_jsxs("span", { className: 'vx-split__word', "aria-hidden": 'true', children: [by === 'word' ? (_jsx(motion.span, { className: 'vx-split__piece', variants: piece, children: word })) : (Array.from(word).map((ch) => {
                    index += 1;
                    return (_jsx(motion.span, { className: 'vx-split__piece', variants: piece, children: ch }, `${ch}-${index}`));
                })), wi < words.length - 1 ? ' ' : null] }, `${word}-${wi}`))) })));
}
/* -------------------------------- Magnetic --------------------------------
 * Pulls its child toward the pointer on fine-pointer devices.
 * ------------------------------------------------------------------------ */
export function Magnetic({ children, strength = 0.32, className, }) {
    const reduced = useReducedMotion();
    const x = useSpring(0, { stiffness: 220, damping: 18, mass: 0.5 });
    const y = useSpring(0, { stiffness: 220, damping: 18, mass: 0.5 });
    const onMove = (e) => {
        if (reduced || e.pointerType !== 'mouse')
            return;
        const r = e.currentTarget.getBoundingClientRect();
        x.set((e.clientX - (r.left + r.width / 2)) * strength);
        y.set((e.clientY - (r.top + r.height / 2)) * strength);
    };
    const reset = () => {
        x.set(0);
        y.set(0);
    };
    return (_jsx(motion.span, { className: cx('vx-magnetic', className), style: { x, y }, onPointerMove: onMove, onPointerLeave: reset, children: children }));
}
/* --------------------------------- CountUp --------------------------------
 * Writes straight to the DOM node — no React render per frame.
 * ------------------------------------------------------------------------ */
export function CountUp({ to, from = 0, duration = 1.6, play, className, decimals = 0, suffix = '', }) {
    const ref = useRef(null);
    const reduced = useReducedMotion();
    const inView = useInView(ref, { once: true, amount: 0.6 });
    const shouldPlay = play !== null && play !== void 0 ? play : inView;
    const last = useRef(from);
    useEffect(() => {
        const node = ref.current;
        if (!node)
            return undefined;
        const target = shouldPlay ? to : from;
        if (reduced) {
            node.textContent = target.toFixed(decimals) + suffix;
            last.current = target;
            return undefined;
        }
        const controls = animate(last.current, target, {
            duration,
            ease: EASE_OUT,
            onUpdate: (v) => {
                last.current = v;
                node.textContent = v.toFixed(decimals) + suffix;
            },
        });
        return () => controls.stop();
    }, [shouldPlay, to, from, duration, reduced, decimals, suffix]);
    return (_jsx("span", { ref: ref, className: className, children: from.toFixed(decimals) + suffix }));
}
/* --------------------------------- Typed ----------------------------------
 * Reveals `text` character by character in step with a MotionValue (0→1).
 * Updates textContent directly so scroll-linked typing never re-renders.
 * ------------------------------------------------------------------------ */
export function Typed({ text, progress, className, caret = true, }) {
    const ref = useRef(null);
    const write = useCallback((v) => {
        const node = ref.current;
        if (!node)
            return;
        const count = Math.round(Math.max(0, Math.min(1, v)) * text.length);
        if (node.dataset.count === String(count))
            return;
        node.dataset.count = String(count);
        node.textContent = text.slice(0, count);
    }, [text]);
    useMotionValueEvent(progress, 'change', write);
    useEffect(() => write(progress.get()), [progress, write]);
    return (_jsxs("span", { className: cx('vx-typed', caret && 'vx-typed--caret', className), children: [_jsx("span", { ref: ref }), _jsx("span", { className: 'vx-sr', children: text })] }));
}
/** Runs a callback on each animation frame while `active` — paused otherwise. */
export function useTicker(active, cb) {
    const cbRef = useRef(cb);
    cbRef.current = cb;
    useEffect(() => {
        if (!active)
            return undefined;
        let raf = 0;
        let last = performance.now();
        const loop = (now) => {
            cbRef.current(Math.min(64, now - last), now);
            last = now;
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, [active]);
}
/** A live HH:MM:SS (or MM:SS) clock that only ticks while visible. */
export function LiveClock({ start, running = true, countdown = false, format = 'hms', className, }) {
    const ref = useRef(null);
    const inView = useInView(ref);
    const value = useRef(start);
    const render = (s) => {
        const t = Math.max(0, Math.floor(s));
        const h = Math.floor(t / 3600);
        const m = Math.floor((t % 3600) / 60);
        const sec = t % 60;
        const pad = (n) => String(n).padStart(2, '0');
        return format === 'hms' ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
    };
    useEffect(() => {
        if (!running || !inView)
            return undefined;
        const id = window.setInterval(() => {
            value.current += countdown ? -1 : 1;
            if (ref.current)
                ref.current.textContent = render(value.current);
        }, 1000);
        return () => window.clearInterval(id);
        // render is pure; excluding it keeps the interval stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [running, inView, countdown]);
    return (_jsx("span", { ref: ref, className: className, children: render(value.current) }));
}
